'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as z from 'zod'
import { Button } from '@/components/ui/button'
import { useDemoMode } from '@/context/DemoModeContext'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useMutation, useQuery } from '@apollo/client'
import {
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { SheetScrollableContent } from '@/components/ui/sheet-scrollable-content'
import '@/components/styles/Form.css'
import InputFieldContainer from '@/components/shared/inputComponents/InputFieldContainer'
import {
  CREATE_ONE_CUSTOMER_ACCOUNT,
  UPDATE_ONE_CUSTOMER_ACCOUNT,
  FIND_UNIQUE_CUSTOMER_ACCOUNT,
  FIND_MANY_CUSTOMER_ACCOUNT,
} from './customerAccountQueries'
import { FIND_MANY_CUSTOMER_SUB_ACCOUNT } from '../CustomerSubAccounts/customerSubAccountQueries'
import { CustomerAccountFormValues } from './types'

const formSchema = z.object({
  email: z.string().email('Invalid email').nonempty('Email is required'),
  password: z.string().optional(),
  name: z.string().optional(),
  notes: z.string().optional(),
  customerSubAccountId: z.number().optional(),
}) satisfies z.ZodType<CustomerAccountFormValues>

interface CustomerAccountFormProps {
  data: {
    id?: string
    customerId: number
    preSelectedLocationId?: string
  }
  onDirtyChange?: (dirty: boolean) => void
  onRequestClose?: () => void
}

export default function CustomerAccountForm({
  data,
  onDirtyChange,
  onRequestClose,
}: CustomerAccountFormProps) {
  const { isDemoMode } = useDemoMode()
  const { id, customerId } = data

  const {
    data: { findUniqueCustomerAccount: customerAccountData } = {},
    loading: loadingData,
  } = useQuery(FIND_UNIQUE_CUSTOMER_ACCOUNT, {
    variables: { id: id! },
    fetchPolicy: 'network-only',
    skip: !id,
  })

  const {
    data: { findManyCustomerSubAccount: customerSubAccounts } = {},
    loading: loadingSubAccounts,
  } = useQuery(FIND_MANY_CUSTOMER_SUB_ACCOUNT, {
    variables: { customerId },
    fetchPolicy: 'cache-and-network',
  })

  const form = useForm<CustomerAccountFormValues>({
    resolver: zodResolver(formSchema),
    mode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
      name: '',
      notes: '',
      customerSubAccountId: undefined,
    },
  })

  const [updateCustomerAccount, { loading: updateLoading }] = useMutation(
    UPDATE_ONE_CUSTOMER_ACCOUNT,
    {
      onError: e => {
        toast.error('Failed to update customer account')
      },
      onCompleted: () => {
        toast.success('Customer account updated successfully')
        form.reset()
        if (onRequestClose) {
          onRequestClose()
        }
      },
      refetchQueries: [
        {
          query: FIND_MANY_CUSTOMER_ACCOUNT,
          variables: { customerId },
        },
      ],
    }
  )

  const [createCustomerAccount, { loading: createLoading }] = useMutation(
    CREATE_ONE_CUSTOMER_ACCOUNT,
    {
      onError: e => {
        console.error('Error creating customer account:', e)

        // Check if the error is about duplicate email
        const isDuplicateEmailError = e.graphQLErrors?.some(
          error =>
            error.message.includes('already exists') &&
            error.message.includes('email')
        )

        if (isDuplicateEmailError) {
          // Set field-specific error for email
          form.setError('email', {
            type: 'manual',
            message: 'An account with this email already exists',
          })
        } else {
          toast.error('Failed to create customer account')
        }
      },
      onCompleted: response => {
        toast.success('Customer account created successfully')
        form.reset()
        if (onRequestClose) {
          onRequestClose()
        }
      },
      refetchQueries: [
        {
          query: FIND_MANY_CUSTOMER_ACCOUNT,
          variables: { customerId },
        },
      ],
    }
  )

  function onSubmit(values: CustomerAccountFormValues) {
    if (id) {
      updateCustomerAccount({
        variables: {
          id,
          email: values.email,
          password: values.password,
          name: values.name,
          notes: values.notes,
          customerSubAccountId: values.customerSubAccountId,
        },
      })
    } else {
      createCustomerAccount({
        variables: {
          customerId,
          email: values.email,
          password: values.password,
          name: values.name,
          notes: values.notes,
          customerSubAccountId: values.customerSubAccountId,
        },
      })
    }
  }

  // Handle form submission with event prevention
  const handleFormSubmit = (event: React.FormEvent) => {
    // Prevent the form submission from bubbling up to parent forms
    event.preventDefault()
    event.stopPropagation()

    // Call the react-hook-form's handleSubmit
    form.handleSubmit(onSubmit)(event)
  }

  useEffect(() => {
    if (customerAccountData) {
      form.reset({
        email: customerAccountData.email,
        password: '', // Don't populate password for security
        name: customerAccountData.name || '',
        notes: customerAccountData.notes || '',
        customerSubAccountId:
          customerAccountData.customerSubAccountId || undefined,
      })
    } else if (!id) {
      // Reset form to empty values when creating a new account
      // If preSelectedLocationId is provided, convert it to number and pre-select it
      const preSelectedLocationIdAsNumber = data.preSelectedLocationId
        ? parseInt(data.preSelectedLocationId, 10)
        : undefined

      form.reset({
        email: '',
        password: '',
        name: '',
        notes: '',
        customerSubAccountId: preSelectedLocationIdAsNumber,
      })
    }
  }, [customerAccountData, form.reset, id, data.preSelectedLocationId])

  useEffect(() => {
    if (onDirtyChange) {
      onDirtyChange(form.formState.isDirty)
    }
  }, [form.formState.isDirty, onDirtyChange])

  return (
    <SheetContent className="sheet-content flex flex-col">
      <SheetHeader className="flex-shrink-0">
        <SheetTitle>
          {id ? 'Edit Customer Account' : 'Add User Account'}
        </SheetTitle>
        {id && <SheetDescription>ID: {id}</SheetDescription>}
      </SheetHeader>
      <SheetScrollableContent>
        <Form {...form}>
          <form
            onSubmit={handleFormSubmit}
            className="space-y-4 max-w-3xl mx-auto py-5"
          >
            <div className="grid gap-4 py-4">
              <InputFieldContainer
                label="Email"
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Email address"
                        type="email"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Password"
                description="Leave empty to keep current password"
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Password (optional)"
                        type="password"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Name"
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Input
                        placeholder="Full name (optional)"
                        {...field}
                        className={isDemoMode ? 'blur-sm select-none' : ''}
                        readOnly={isDemoMode}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Location"
                control={form.control}
                name="customerSubAccountId"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Select
                        onValueChange={value => {
                          field.onChange(
                            value === 'none' ? undefined : parseInt(value, 10)
                          )
                        }}
                        value={
                          field.value !== undefined
                            ? String(field.value)
                            : 'none'
                        }
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select a location (optional)" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none" key={'none'}>
                            No location
                          </SelectItem>
                          {customerSubAccounts?.map((location: any) => (
                            <SelectItem
                              key={location.id}
                              value={String(location.id)}
                            >
                              {location.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <InputFieldContainer
                label="Notes"
                control={form.control}
                name="notes"
                render={({ field }) => (
                  <FormItem className="col-span-3">
                    <FormControl>
                      <Textarea
                        placeholder="Additional notes (optional)"
                        className="resize-none"
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <Button
                type="submit"
                disabled={loadingData || updateLoading || createLoading}
              >
                {updateLoading || createLoading ? (
                  <div className="flex items-center gap-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {id ? 'Updating...' : 'Creating...'}
                  </div>
                ) : id ? (
                  'Update Account'
                ) : (
                  'Create Account'
                )}
              </Button>
            </div>
          </form>
        </Form>
      </SheetScrollableContent>
    </SheetContent>
  )
}
